export const META_PIXEL_ID = '27931902216435024'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

/**
 * Loads the Meta Pixel (async, non-blocking) and fires the initial PageView.
 * Safe to call more than once — the loader short-circuits if fbq already exists.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  try {
    load()
  } catch {
    /* analytics must never break the page */
  }
}

function load(): void {
  if (!window.fbq) {
    const n: Fbq = function (...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod.apply(n, args)
      } else {
        n.queue?.push(args)
      }
    } as Fbq

    window.fbq = n
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const first = document.getElementsByTagName('script')[0]
    if (first?.parentNode) {
      first.parentNode.insertBefore(script, first)
    } else {
      document.head.appendChild(script)
    }

    // <noscript> fallback beacon, mirroring the standard snippet.
    const noscript = document.createElement('noscript')
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.alt = ''
    img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    if (document.body) document.body.appendChild(noscript)
  }

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

interface AddToCartPayload {
  value: number
  currency: string
  contentId: string
  contentName: string
}

/** Fires the standard Meta AddToCart event for a CTA click. */
export function trackAddToCart({
  value,
  currency,
  contentId,
  contentName,
}: AddToCartPayload): void {
  if (typeof window === 'undefined') return

  // The pixel may not be loaded yet on a very fast tap: initialise on demand
  // so the event is queued, then let the navigation continue immediately.
  if (!window.fbq) initMetaPixel()

  window.fbq?.('track', 'AddToCart', {
    value,
    currency,
    content_type: 'product',
    content_ids: [contentId],
    content_name: contentName,
    contents: [{ id: contentId, quantity: 1, item_price: value }],
    num_items: 1,
  })
}
